// Finds receipts lying on a scanner bed or table: regions that stand out from the page's border color, each
// fitted with its smallest rotated rectangle. It works on a small rendering of the page and suggests boxes only;
// white receipts on white paper don't stand out and yield nothing.
const DIFFERENCE = 24
const CLOSE_RADIUS = 2
const MIN_AREA_SHARE = 0.004
// A line of text closes into a long, thin region; a receipt is never that narrow.
const MIN_SIDE_SHARE = 0.05
// Receipt paper differs from the bed almost everywhere; printing on a plain page only where the ink is.
const MIN_SOLIDITY = 0.8
const PAD_SHARE = 0.008

const luminance = ({ data, width, height }) => {
  const gray = new Float32Array(width * height)
  for (let i = 0; i < gray.length; i++) gray[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2]
  return gray
}

const borderMedian = (gray, width, height) => {
  const values = []
  for (let x = 0; x < width; x++) values.push(gray[x], gray[(height - 1) * width + x])
  for (let y = 0; y < height; y++) values.push(gray[y * width], gray[y * width + width - 1])
  values.sort((a, b) => a - b)
  return values[Math.floor(values.length / 2)]
}

// Max (dilate) or min (erode) over a square window, done as two one-dimensional passes.
const morph = (mask, width, height, radius, pick) => {
  const pass = (source, horizontal) => {
    const out = new Uint8Array(source.length)
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let value = pick === 'max' ? 0 : 1
        for (let d = -radius; d <= radius; d++) {
          const nx = horizontal ? x + d : x
          const ny = horizontal ? y : y + d
          const inside = nx >= 0 && ny >= 0 && nx < width && ny < height
          const sample = inside ? source[ny * width + nx] : pick === 'max' ? 0 : 1
          value = pick === 'max' ? Math.max(value, sample) : Math.min(value, sample)
        }
        out[y * width + x] = value
      }
    }
    return out
  }
  return pass(pass(mask, true), false)
}

// Each region's leftmost and rightmost pixel per row: enough to find its outline, without keeping every pixel.
const regions = (mask, raw, width, height) => {
  const labels = new Int32Array(width * height)
  const found = []
  const stack = []

  for (let start = 0; start < mask.length; start++) {
    if (!mask[start] || labels[start]) continue
    const region = { area: 0, solid: 0, rows: new Map(), minX: width, maxX: 0, minY: height, maxY: 0 }
    labels[start] = found.length + 1
    stack.push(start)

    while (stack.length) {
      const i = stack.pop()
      const x = i % width
      const y = (i - x) / width
      region.area++
      region.solid += raw[i]
      region.minX = Math.min(region.minX, x)
      region.maxX = Math.max(region.maxX, x)
      region.minY = Math.min(region.minY, y)
      region.maxY = Math.max(region.maxY, y)
      const row = region.rows.get(y)
      region.rows.set(y, row ? [Math.min(row[0], x), Math.max(row[1], x)] : [x, x])

      const neighbors = [x > 0 && i - 1, x < width - 1 && i + 1, y > 0 && i - width, y < height - 1 && i + width]
      neighbors.forEach((n) => {
        if (n !== false && mask[n] && !labels[n]) {
          labels[n] = labels[i]
          stack.push(n)
        }
      })
    }
    found.push(region)
  }
  return found
}

const rotate = ({ x, y }, degrees) => {
  const a = (degrees * Math.PI) / 180
  return { x: x * Math.cos(a) - y * Math.sin(a), y: x * Math.sin(a) + y * Math.cos(a) }
}

// The smallest rectangle around the points, trying each whole-degree tilt.
const smallestRectangle = (points) => {
  let best = null
  for (let angle = 0; angle < 90; angle++) {
    const local = points.map((p) => rotate(p, -angle))
    const xs = local.map((p) => p.x)
    const ys = local.map((p) => p.y)
    const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)]
    const area = (maxX - minX) * (maxY - minY)
    if (!best || area < best.area) {
      const center = rotate({ x: (minX + maxX) / 2, y: (minY + maxY) / 2 }, angle)
      best = { area, angle, cx: center.x, cy: center.y, w: maxX - minX, h: maxY - minY }
    }
  }
  return best
}

// Prefer the smaller tilt, and stand a long, narrow receipt upright; an upside-down one still needs turning by hand.
const upright = ({ angle, cx, cy, w, h }) => {
  let box = angle > 45 ? { cx, cy, w: h, h: w, angle: angle - 90 } : { cx, cy, w, h, angle }
  if (box.w > box.h * 1.2) box = { ...box, w: box.h, h: box.w, angle: box.angle + 90 }
  return { ...box, angle: ((Math.round(box.angle) % 360) + 360) % 360 }
}

// image is { data, width, height } (RGBA, like ImageData); scale is image pixels per page unit.
export const detectBoxes = (image, scale) => {
  const { width, height } = image
  const gray = luminance(image)
  const background = borderMedian(gray, width, height)
  const raw = new Uint8Array(gray.length)
  for (let i = 0; i < gray.length; i++) raw[i] = Math.abs(gray[i] - background) > DIFFERENCE ? 1 : 0
  const mask = morph(morph(raw, width, height, CLOSE_RADIUS, 'max'), width, height, CLOSE_RADIUS, 'min')

  const minArea = width * height * MIN_AREA_SHARE
  const minSide = Math.max(width, height) * MIN_SIDE_SHARE
  const pad = Math.max(width, height) * PAD_SHARE

  return regions(mask, raw, width, height)
    .filter((r) => r.area >= minArea && r.solid / r.area >= MIN_SOLIDITY)
    .filter((r) => !(r.maxX - r.minX > width * 0.95 && r.maxY - r.minY > height * 0.95))
    .map((r) => {
      const outline = [...r.rows.entries()].flatMap(([y, [left, right]]) => [
        { x: left, y }, { x: right + 1, y }, { x: left, y: y + 1 }, { x: right + 1, y: y + 1 }
      ])
      return upright(smallestRectangle(outline))
    })
    .filter((box) => Math.min(box.w, box.h) >= minSide)
    .map((box) => ({
      cx: box.cx / scale,
      cy: box.cy / scale,
      w: (box.w + pad * 2) / scale,
      h: (box.h + pad * 2) / scale,
      angle: box.angle
    }))
}
